import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import { createGame } from "../../services/api/games";
import { getMyLeagues, getRinks } from "../../services/api/leagues";
import { loadErrorMessage } from "../../utils/api-error";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

/** Returns array of dayjs dates between start and end (inclusive) on selected weekdays, at the given time. */
function getRepeatingDates(startDate, endDate, selectedDays, timeOfDay) {
  const dates = [];
  const hour = timeOfDay.hour();
  const minute = timeOfDay.minute();
  let d = startDate.startOf("day");
  const end = endDate.endOf("day");
  while (d.isBefore(end) || d.isSame(end, "day")) {
    if (selectedDays.includes(d.day())) {
      dates.push(d.hour(hour).minute(minute).second(0).millisecond(0));
    }
    d = d.add(1, "day");
  }
  return dates;
}

export default function CreateGameView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leagues, setLeagues] = React.useState([]);
  const [leagueId, setLeagueId] = React.useState("");
  const [showWelcomeDialog, setShowWelcomeDialog] = React.useState(false);
  const [rinks, setRinks] = React.useState([]);
  const [rinkId, setRinkId] = React.useState("");
  const [repeatEnabled, setRepeatEnabled] = React.useState(false);
  const [startsAt, setStartsAt] = React.useState(() => dayjs().add(1, "day").hour(19).minute(0).second(0).millisecond(0));
  const [repeatStartDate, setRepeatStartDate] = React.useState(() => dayjs().add(1, "day"));
  const [repeatEndDate, setRepeatEndDate] = React.useState(() => dayjs().add(1, "month"));
  const [repeatTime, setRepeatTime] = React.useState(() => dayjs().hour(19).minute(0).second(0).millisecond(0));
  const [repeatDays, setRepeatDays] = React.useState({ 0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false });
  const [durationMinutes, setDurationMinutes] = React.useState("60");
  const [arenaName, setArenaName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [loadLeaguesError, setLoadLeaguesError] = React.useState("");
  const [error, setError] = React.useState("");
  const [saveSuccessMessage, setSaveSuccessMessage] = React.useState("");

  React.useEffect(() => {
    getMyLeagues()
      .then((list) => setLeagues(Array.isArray(list) ? list : []))
      .catch((err) => setLoadLeaguesError(loadErrorMessage(err, "Failed to load leagues.")));
  }, []);

  const fromNewLeagueRef = React.useRef(false);
  React.useEffect(() => {
    const state = location.state;
    if (!state) return;
    if (state?.fromNewLeague && !fromNewLeagueRef.current) {
      fromNewLeagueRef.current = true;
      if (state.leagueId) {
        setLeagueId(state.leagueId);
      }
      setShowWelcomeDialog(true);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (state?.leagueId && !fromNewLeagueRef.current) {
      fromNewLeagueRef.current = true;
      setLeagueId(state.leagueId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  React.useEffect(() => {
    if (!leagueId.trim()) {
      setRinks([]);
      setRinkId("");
      return;
    }
    getRinks(leagueId)
      .then((list) => {
        setRinks(Array.isArray(list) ? list : []);
        setRinkId("");
      })
      .catch(() => {
        setRinks([]);
        setRinkId("");
      });
  }, [leagueId]);

  const handleRepeatDayChange = (dayNum) => (e) => {
    setRepeatDays((prev) => ({ ...prev, [dayNum]: e.target.checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedLeagueId = leagueId.trim();
    if (!trimmedLeagueId) {
      setError("Please select a league.");
      return;
    }
    const duration = parseInt(durationMinutes, 10);
    if (!Number.isInteger(duration) || duration < 1) {
      setError("Duration must be at least 1 minute.");
      return;
    }

    const gamePayload = {
      leagueId: trimmedLeagueId,
      rinkId: rinkId.trim() || undefined,
      durationMinutes: duration,
      arenaName: arenaName.trim() || undefined,
    };

    let datesToCreate = [];
    if (repeatEnabled) {
      if (!repeatStartDate || !repeatStartDate.isValid()) {
        setError("Please select a valid repeat start date.");
        return;
      }
      if (!repeatEndDate || !repeatEndDate.isValid()) {
        setError("Please select a valid repeat end date.");
        return;
      }
      if (repeatEndDate.isBefore(repeatStartDate, "day")) {
        setError("End date must be on or after start date.");
        return;
      }
      const selectedDayNums = DAYS_OF_WEEK.filter((d) => repeatDays[d.value]).map((d) => d.value);
      if (selectedDayNums.length === 0) {
        setError("Select at least one day of the week for the repeat.");
        return;
      }
      datesToCreate = getRepeatingDates(repeatStartDate, repeatEndDate, selectedDayNums, repeatTime);
      if (datesToCreate.length === 0) {
        setError("No dates fall in the range for the selected days.");
        return;
      }
    } else {
      if (!startsAt || !startsAt.isValid()) {
        setError("Please select a valid date and time.");
        return;
      }
      datesToCreate = [startsAt];
    }

    const goToMyLeagues = e.nativeEvent?.submitter?.getAttribute("name") === "saveAndDone";

    setError("");
    setSaveSuccessMessage("");
    setLoading(true);
    try {
      let count = 0;
      for (const start of datesToCreate) {
        await createGame({
          ...gamePayload,
          startsAt: start.toISOString(),
        });
        count += 1;
      }
      setSaveSuccessMessage(count === 1 ? "Game saved." : `${count} games saved.`);
      setTimeout(() => setSaveSuccessMessage(""), 4000);
      resetGameFields();
      if (goToMyLeagues) {
        navigate(`/manage-league/${leagueId}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create game(s).");
    } finally {
      setLoading(false);
    }
  };

  const resetGameFields = () => {
    setStartsAt(dayjs().add(1, "day").hour(19).minute(0).second(0).millisecond(0));
    setRepeatStartDate(dayjs().add(1, "day"));
    setRepeatEndDate(dayjs().add(1, "month"));
    setRepeatTime(dayjs().hour(19).minute(0).second(0).millisecond(0));
    setRepeatDays({ 0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false });
    setDurationMinutes("60");
    setArenaName("");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Dialog open={showWelcomeDialog} onClose={() => setShowWelcomeDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>League created — a couple things before you go live</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <p>
                Your league is set up! Before skaters start joining, make sure you do two things:
              </p>
              <p>
                <strong>1. Add your games</strong> — skaters can&apos;t sign up until there are games scheduled.
              </p>
              <p>
                <strong>2. Set up a waiver</strong> — this is important. A signed waiver protects you as the
                league organizer from being held responsible if a participant gets injured during a game.
                Don&apos;t skip this one — you don&apos;t want to get stuck with the blame.
              </p>
              <p>
                Head to your league&apos;s management page to get both done.
              </p>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowWelcomeDialog(false);
                navigate(`/manage-league/${leagueId}`);
              }}
              variant="contained"
              autoFocus
            >
              Go to my league
            </Button>
          </DialogActions>
        </Dialog>
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h4" component="h3">
              Create a game
            </Typography>
            {leagueId && (
              <Button variant="outlined" size="small" onClick={() => navigate(`/manage-league/${leagueId}`)}>
                Back to league
              </Button>
            )}
          </Stack>
          <Typography color="text.secondary">
            Add a pickup or league game, or series of games, to your league.
          </Typography>

          <Card variant="outlined">
            <CardContent>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <FormControl fullWidth required>
                    <InputLabel id="create-game-league-label">League</InputLabel>
                    <Select
                      labelId="create-game-league-label"
                      label="League"
                      value={leagueId}
                      onChange={(e) => setLeagueId(e.target.value)}
                      disabled={leagues.length === 0}
                    >
                      {leagues.map((league) => (
                        <MenuItem key={league.id} value={league.id}>
                          {league.name}
                          {league.city && league.state ? ` (${league.city}, ${league.state})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {loadLeaguesError ? (
                    <Alert severity="warning">{loadLeaguesError}</Alert>
                  ) : null}

                  {leagueId && (
                    <FormControl fullWidth>
                      <InputLabel id="create-game-rink-label">Rink</InputLabel>
                      <Select
                        labelId="create-game-rink-label"
                        label="Rink"
                        value={rinkId}
                        onChange={(e) => setRinkId(e.target.value)}
                        disabled={rinks.length === 0}
                      >
                        <MenuItem value="">
                          <em>{rinks.length === 0 ? "No rinks — add rinks in league" : "Select a rink"}</em>
                        </MenuItem>
                        {rinks.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                            {r.address ? ` — ${r.address}` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                      {rinks.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Add rinks when creating or editing the league so games can be assigned to a location.
                        </Typography>
                      ) : null}
                    </FormControl>
                  )}

                  {leagueId && (
                    <TextField
                      label="Arena / rink display name override (optional)"
                      fullWidth
                      value={arenaName}
                      onChange={(e) => setArenaName(e.target.value)}
                      placeholder="Override display name for this game only"
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={repeatEnabled}
                        onChange={(e) => setRepeatEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Repeat game (create multiple games)"
                  />

                  {!repeatEnabled ? (
                    <DateTimePicker
                      label="Date & time"
                      value={startsAt}
                      onChange={(newValue) => setStartsAt(newValue)}
                      minDateTime={dayjs()}
                      slotProps={{ textField: { fullWidth: true, required: true } }}
                    />
                  ) : (
                    <>
                      <DatePicker
                        label="Repeat start date"
                        value={repeatStartDate}
                        onChange={(newValue) => setRepeatStartDate(newValue)}
                        minDate={dayjs()}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                      <DatePicker
                        label="Repeat end date"
                        value={repeatEndDate}
                        onChange={(newValue) => setRepeatEndDate(newValue)}
                        minDate={repeatStartDate ?? dayjs()}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                      <TimePicker
                        label="Time"
                        value={repeatTime}
                        onChange={(newValue) => setRepeatTime(newValue)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Repeat on days</FormLabel>
                        <FormGroup row>
                          {DAYS_OF_WEEK.map((d) => (
                            <FormControlLabel
                              key={d.value}
                              control={
                                <Checkbox
                                  checked={repeatDays[d.value] ?? false}
                                  onChange={handleRepeatDayChange(d.value)}
                                  color="primary"
                                />
                              }
                              label={d.label}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </>
                  )}

                  <TextField
                    label="Duration (minutes)"
                    type="number"
                    required
                    fullWidth
                    inputProps={{ min: 1 }}
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    placeholder="60"
                  />
                  {saveSuccessMessage ? (
                    <Alert severity="success" onClose={() => setSaveSuccessMessage("")}>
                      {saveSuccessMessage}
                    </Alert>
                  ) : null}
                  {error ? (
                    <Alert severity="error" onClose={() => setError("")}>
                      {error}
                    </Alert>
                  ) : null}
                  <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || leagues.length === 0}
                    >
                      {loading ? "Saving…" : repeatEnabled ? "Save and add more" : "Save and add another"}
                    </Button>
                    <Button
                      type="submit"
                      name="saveAndDone"
                      variant="outlined"
                      disabled={loading || leagues.length === 0}
                    >
                      {loading ? "Saving…" : "Save"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </LocalizationProvider>
  );
}
